<template>
  <div class="account-settings-container">
    <div class="content">
      <div class="header">
        <div class="logo-category">
          <div class="title">{{ $t('accountPicture') }}</div>
          <div class="logo-container">
            <div v-if="user && user.firstName && !user.client.imageUrl" class="picture">
              {{ $utils.choosePictureName(user.client.name) }}
            </div>
            <img v-else-if="user" class="picture" :src="user.client.imageUrl" />
            <div class="info">
              <div class="import main-button" @click="openInput">
                {{ $t('importPicture') }}
              </div>
              <div class="text">{{ $t('pictureRequirement') }}</div>
              <div class="text">{{ $t('pictureFormat') }}</div>
            </div>
          </div>
        </div>

        <div class="account-category">
          <div class="title">{{ $t('accountName') }}</div>
          <div class="input-container">
            <input ref="inputName" v-model="accountName" type="text" class="input" @keydown.enter="changeName" />
            <div class="icon-container">
              <IconSave class="icon-save icon" :class="{ active: user.client.name !== accountName }" @click="changeName" />
            </div>
          </div>
        </div>

        <div class="account-category id">
          <div class="title">{{ $t('id') }}</div>
          <div class="input-container disabled">
            <input ref="inputName" v-model="id" type="text" class="input" disabled />
            <div class="icon-container"></div>
          </div>
        </div>
      </div>
      <EmailPreferences v-if="user.isMainUser" :company="client" />
      <DefaultMonitoringLevel v-if="user.isMainUser" class="monitoring-level" :company="client" />

      <div class="container">
        <div class="line">
          <div class="title">{{ $t('monitoringLevel') }}</div>
          <RouterLink class="add main-button" to="/settings/account/add">
            {{ $t('addMonitoringLevel') }}
          </RouterLink>
        </div>
        <MonitoringTable />
      </div>
    </div>
    <input :ref="'inputFileSettings'" type="file" accept=".jpg, .jpeg, .png" class="input-file" hidden @change="uploadFile({}, $event)" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import DefaultMonitoringLevel from '@/components/DefaultMonitoringLevel';
import EmailPreferences from '@/components/EmailPreferences';
import MonitoringTable from '@/components/tables/MonitoringTable';

export default {
  components: { MonitoringTable, EmailPreferences, DefaultMonitoringLevel },

  data() {
    return {
      accountName: '',
      loading: false,
      error: false,
      errorMessage: null,
      id: '',
      client: null,
    };
  },

  computed: {
    ...mapGetters(['user']),
  },

  async mounted() {
    this.$refs['inputName'].value = this.user.client.name;
    this.accountName = this.user.client.name;
    this.id = this.user.client.code;
    this.client = await this.$store.dispatch('getUsers', { id: this.user.client.id });
  },

  methods: {
    async uploadFile(_, event) {
      if (!event.target.files[0]) {
        return;
      }
      this.fileName = event.target.files[0].name;
      const data = new FormData();
      data.append('Image', event.target.files[0]);
      this.file = data;
      await this.$store.dispatch('setImageCompany', data);
      window.location.reload();
    },

    openInput() {
      this.$refs[`inputFileSettings`].click();
    },

    changeName() {
      this.$store
        .dispatch('editClient', {
          id: this.user.client.id,
          code: this.id,
          name: this.accountName,
        })
        .then(() => {
          this.$store.dispatch('getUser');
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.account-settings-container {
  margin: 60px 0 0;

  .content .header {
    display: flex;
    margin: 0 0 50px;

    .logo-category {
      .title {
        color: colors.$darkblue-color;
        font-weight: 500;
        font-size: 16px;
        margin-bottom: 20px;
      }
      .logo-container {
        display: flex;

        .picture {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 70px;
          height: 70px;
          border-radius: 50%;
          color: colors.$black-color;
          background: colors.$white-color;
          margin: 0 32px 0 0;
          font-weight: 800;
          font-size: 28px;
        }

        .info {
          margin: 0 128px 0 0;

          .import {
            background: colors.$lightblue-color;
            border-radius: 5px;
            margin-bottom: 10px;
            height: 30px;
            padding: 4px;
            width: 160px;
          }

          .text {
            font-size: 14px;
            color: colors.$lightblue-color;
          }
        }
      }
    }

    .account-category {
      &.id {
        margin-left: 16px;
      }

      .title {
        color: colors.$darkblue-color;
        font-weight: 500;
        font-size: 16px;
      }

      .input-container {
        margin: 0 0 24px;
        display: flex;

        &.disabled {
          .input,
          .icon-container {
            background: #f8fbff;
            color: rgba(black, 0.5);
          }
        }

        .icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 46px;
          margin: 16px 0 0;
          padding: 0;
          color: colors.$lightblue-color;
          border-radius: 4px;
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
          border: solid 1px colors.$lightgrey-color;
          border-left: none;

          .icon {
            width: 24px;
            height: 24px;
            color: colors.$lightgrey-color;

            &.active {
              color: colors.$lightblue-color;
            }
          }

          &:hover {
            cursor: pointer;

            .icon-search {
              transform: scale(1.1);
            }
          }
        }

        .input {
          width: 100%;
          height: 48px;
          margin: 16px 0 0;
          padding: 12px 16px;
          box-sizing: border-box;
          color: colors.$text-color;
          border: solid 1px colors.$lightgrey-color;
          border-radius: 4px;
          background: colors.$white-color;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          border-right: none;

          &::placeholder {
            color: colors.$lightgrey-color;
          }
        }
      }
    }
  }

  .content .container {
    .line {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .title {
        color: colors.$darkblue-color;
        font-weight: 500;
        font-size: 20px;
      }

      .add {
        width: 270px;
        padding: 0;
      }
    }
  }
}

.monitoring-level {
  width: 30%;
}

@media only screen and (max-width: responsive.$layout-breakpoint-small) {
  .line {
    flex-direction: column;
    align-items: flex-start !important;

    height: 82px;
  }

  .table-container {
    margin-top: 32px;
  }

  .header {
    flex-direction: column;
    margin: 12px 0 !important;

    .account-category {
      margin-top: 24px;

      &.id {
        margin-left: 0 !important;
      }
    }
  }
}
</style>
